import clsx from 'clsx';
import { useMemo, type JSX } from 'react';
import { FormField } from '@/components/form';
import { calculateMonthlyRent } from '@/lib/utils/rentCalculations';
import type { UnitFormErrors, UnitFormState } from './unitFormState';
import { formatRentInputValue, sanitizeRentInput } from './rentInput';

interface UnitPricingSectionProps {
  formState: UnitFormState;
  errors: UnitFormErrors;
  disabled: boolean;
  isUploadingHero: boolean;
  setFormState: React.Dispatch<React.SetStateAction<UnitFormState>>;
  clearFieldError: (field: keyof UnitFormState) => void;
}

const inputClass =
  'rounded-2xl border border-surface-subtle px-4 py-2.5 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:cursor-not-allowed disabled:opacity-60 md:py-2 md:text-sm';

export function UnitPricingSection({
  formState,
  errors,
  disabled,
  isUploadingHero,
  setFormState,
  clearFieldError,
}: UnitPricingSectionProps): JSX.Element {
  const additionalRentDisabled = disabled || isUploadingHero;
  const monthlyRent = useMemo(() => {
    return calculateMonthlyRent({
      netRentPerSf: toNumber(formState.netRentPerSf),
      additionalRentPerSf: toNumber(formState.additionalRentPerSf),
      unitSqFt: toNumber(formState.areaSf),
      rentFrequency: 'annually',
    });
  }, [formState.additionalRentPerSf, formState.areaSf, formState.netRentPerSf]);
  const monthlyRentLabel = useMemo(() => {
    if (monthlyRent === null) {
      return '—';
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 2,
    }).format(monthlyRent);
  }, [monthlyRent]);

  return (
    <section
      className="flex flex-col gap-4"
      data-testid="unit-pricing-section"
      data-ui="Suites.Edit.RentSection"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          id="unit-monthly-rent"
          label="Monthly rent (derived)"
          description="Calculated from annual NET $/SF, annual Additional Rent $/SF, and Unit Sq Ft."
          className="md:col-span-2"
        >
          <input
            type="text"
            value={monthlyRentLabel}
            readOnly
            aria-readonly="true"
            className={clsx(inputClass, 'bg-surface-muted/60 text-text-primary/70')}
          />
        </FormField>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          id="unit-net-rent"
          label="Annual net rent / SF"
          description="Shown as the annual base $/SF rate on public listings. Accepts up to two decimals, e.g., 24.50."
          error={errors.netRentPerSf}
          required
        >
          <input
            type="text"
            inputMode="decimal"
            value={formState.netRentPerSf}
            onChange={(event) => {
              const value = sanitizeRentInput(event.target.value);
              setFormState((prev) => ({ ...prev, netRentPerSf: value }));
              clearFieldError('netRentPerSf');
            }}
            onBlur={() => {
              setFormState((prev) => ({
                ...prev,
                netRentPerSf: formatRentInputValue(prev.netRentPerSf),
              }));
            }}
            disabled={disabled || isUploadingHero}
            className={inputClass}
          />
        </FormField>

        <FormField
          id="unit-additional-rent"
          label="Annual additional rent / SF"
          description="Shown as the annual add-on $/SF rate in public rent details when applicable. Accepts up to two decimals, e.g., 8.00."
          error={errors.additionalRentPerSf}
        >
          <input
            type="text"
            inputMode="decimal"
            value={formState.additionalRentPerSf}
            onChange={(event) => {
              const value = sanitizeRentInput(event.target.value);
              setFormState((prev) => ({ ...prev, additionalRentPerSf: value }));
              clearFieldError('additionalRentPerSf');
            }}
            onBlur={() => {
              setFormState((prev) => ({
                ...prev,
                additionalRentPerSf: formatRentInputValue(prev.additionalRentPerSf),
              }));
            }}
            disabled={additionalRentDisabled}
            className={inputClass}
          />
        </FormField>
      </div>
    </section>
  );
}

function toNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}
